import React, { useContext, useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import moment from "moment";
import { UserContext } from "../../../service/context";
import ReCAPTCHA from "react-google-recaptcha";

// Redux
import { useDispatch } from "react-redux";
import { login } from "../../../redux/actions/auth";
import { LOGIN } from "../../../redux/constants";

function UserLogin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const ctx = useContext(UserContext);
  const [showPassword, setShowPassword] = useState(false);
  const [authUser, setAuthUser] = useState({});
  const [reCaptchaToken, setReCaptchaToken] = useState();
  const [isCaptchaChecked, setIsCaptchaChecked] = useState(false);

  const captchaRef = useRef(null);

  // Function to store the captcha token
  const handleCaptchaClick = (token) => {
    setReCaptchaToken(token);
    setIsCaptchaChecked(!isCaptchaChecked);
  };

  const schema = yup.object({
    email: yup
      .string()
      .required("Email is required.")
      .email("Please enter valid email address."),
    password: yup
      .string()
      .required("Password is required.")
      .matches(
        /^[^\s]+(?:$|.*[^\s]+$)/,
        "Blank space not allowed in company name."
      ),
    rememberme: yup.boolean().notRequired(),
  });

  const { control, handleSubmit, reset } = useForm({
    resolver: yupResolver(schema),
  });

  const defaultValues = {
    email: "",
    password: "",
    rememberme: false,
  };

  // useEffect to load the google reCaptcha script once component is fully rendered
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [])
  

  useEffect(() => {
    if (Object.keys(authUser).includes("is_payment_complete")) {
      dispatch({ type: LOGIN, payload: authUser });
      if (authUser.is_board_complete && authUser.is_payment_complete) {
        navigate(`/booking/overview`);
      } else {
        navigate(`/onboard`);
      }
    }

    // Below return is commented due to below error.
    // Warning: Maximum update depth exceeded. This can happen when a component calls setState inside useEffect, but useEffect either doesn't have a dependency array, or one of the dependencies changes on every render.

    // return () => {
    //   setAuthUser({});
    // };
  }, [authUser]);

  const onSubmit = async (data) => {
    setLoading(true);
    data.type = "user";
    data.captchaToken = reCaptchaToken
    dispatch(
      login(data, (res) => {
        setLoading(false);
        reset(defaultValues);
        captchaRef?.current?.reset()
        
        if (!res.status) {
          setError(res.message);
          setIsCaptchaChecked(false);
        }
        if (res.status) {
          const user = {
            ...res.data,
            expire: moment().add(1, "day"),
          };
          ctx.login(user);
          localStorage.setItem("user", JSON.stringify(user));

          if (user.type === "super-admin") {
            if (user.is_board_complete && user.is_subscribed) {
              navigate(`/booking/overview`);
            } else {
              navigate(`/onboard`);
            }
          } else {
            if (user.type === "admin") {
              navigate(`/booking/overview`);
            } else {
              navigate(`/`);
            }
          }
        }
      })
    );
  };

  return (
    <div className="mt-5">
      {/* Warning */}
      {error && (
        <div className="bg-amber-100 text-amber-600 px-3 py-2 rounded mb-2">
          <svg
            className="inline w-3 h-3 shrink-0 fill-current mr-2"
            viewBox="0 0 12 12"
          >
            <path d="M10.28 1.28L3.989 7.575 1.695 5.28A1 1 0 00.28 6.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 1.28z" />
          </svg>
          <span className="text-sm">{error}</span>
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-4">
          <Controller
            name="email"
            defaultValue={defaultValues.email}
            control={control}
            render={({ field, formState }) => (
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="email"
                >
                  Email Address
                </label>
                <input
                  {...field}
                  id="email"
                  className="form-input w-full"
                  type="email"
                />
                {formState.errors?.email && (
                  <div className="text-xs text-rose-600 pt-1 pl-1">
                    {formState.errors?.email?.message}
                  </div>
                )}
              </div>
            )}
          />

          <Controller
            name="password"
            defaultValue={defaultValues.password}
            control={control}
            render={({ field, formState }) => (
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="password"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    {...field}
                    className="form-input w-full"
                    type={showPassword ? "text" : "password"}
                    autoComplete="off"
                  />
                  <div class="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5">
                    {showPassword && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="w-4 h-4 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowPassword(!showPassword);
                        }}
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    )}
                    {!showPassword && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="w-4 h-4 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowPassword(!showPassword);
                        }}
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                        />
                      </svg>
                    )}
                  </div>
                </div>
                {formState.errors?.password && (
                  <div className="text-xs text-rose-600 pt-1 pl-1">
                    {formState.errors?.password?.message}
                  </div>
                )}
              </div>
            )}
          />
        </div>
        <div className="flex items-center justify-between my-6">
          <Controller
            name="rememberme"
            defaultValue={defaultValues.rememberme}
            control={control}
            render={({ field, formState }) => (
              <div className="mr-1">
                <label className="flex items-center">
                  <input
                    {...field}
                    // name="rememberMe"
                    type="checkbox"
                    className="form-checkbox"
                  />
                  <span className="text-sm ml-2">Remember me</span>
                </label>
              </div>
            )}
          />

          <div className="mr-1">
            <Link
              className="text-sm text-indigo-600 hover:text-indigo-300 "
              to="/reset-psw"
            >
              Forgot Password?
            </Link>
          </div>
        </div>

        <ReCAPTCHA
          sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
          onChange={handleCaptchaClick}
          ref={captchaRef}
        />

        {isCaptchaChecked && (
          <button
            type="submit"
            disabled={loading}
            className={`flex items-center btn ${
              loading
                ? "!bg-indigo-300 pointer-events-none"
                : "!bg-indigo-500 hover:!bg-indigo-600"
            } text-white w-full mt-5`}
          >
            Sign In
            {loading && (
              <span className="ml-2">
                <svg
                  className="animate-spin w-4 h-4 fill-current shrink-0"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                </svg>
              </span>
            )}
          </button>
        )}
      </form>
    </div>
  );
}

export default UserLogin;
